<script lang="ts">
	import { priorityLayoutDevHudKind } from 'src/stores/features/dev/priority-layout-dev-hud-state';
	import EmeterPriorityDevHud from 'src/components/Sidepanel/DeviceViews/Emeter/EmeterPriorityDevHud.svelte';
	import SensorPriorityDevHud from 'src/components/Sidepanel/DeviceViews/Sensor/SensorPriorityDevHud.svelte';
	import RelayPriorityDevHud from 'src/components/Sidepanel/DeviceViews/Relay/RelayPriorityDevHud.svelte';
	import LightPriorityDevHud from 'src/components/Sidepanel/DeviceViews/Light/LightPriorityDevHud.svelte';
	import DevModal from './DevModal.svelte';
	import WidgetTypeDebugLegend from './WidgetTypeDebugLegend.svelte';

	const devEnabled =
		import.meta.env.DEV ||
		import.meta.env.MODE === 'inspect' ||
		['alpha', 'sigma', 'omega', 'gamma', 'bravo'].includes(import.meta.env.VITE_APP_BUILD);

	let openDevModal = false;

	// Available on logged-in and public (login/signup) surfaces when the build allows it.
	$: visible = devEnabled;

	// Left-edge pull tab (mobile/tablet): mostly off-screen at rest; pull right to reveal.
	const REST_HIDDEN = 40;
	const PULL_MAX = 40;
	const PULL_THRESHOLD = 28;
	let dragOffset = 0;
	let touchStartX = 0;
	let dragTriggered = false;
	let snapping = false;

	$: edgeTranslateX = dragOffset - REST_HIDDEN;

	const snapBack = () => {
		snapping = true;
		dragOffset = 0;
		window.setTimeout(() => (snapping = false), 260);
	};

	const onEdgeTouchStart = (event: TouchEvent) => {
		touchStartX = event.touches[0]?.clientX ?? 0;
		dragTriggered = false;
		snapping = false;
		dragOffset = 0;
	};

	const onEdgeTouchMove = (event: TouchEvent) => {
		if (dragTriggered) return;
		const currentX = event.touches[0]?.clientX ?? touchStartX;
		const delta = currentX - touchStartX;
		if (delta <= 0) {
			dragOffset = 0;
			return;
		}
		if (event.cancelable) event.preventDefault();
		dragOffset = Math.min(delta, PULL_MAX);
		if (delta >= PULL_THRESHOLD) {
			dragTriggered = true;
			openDevModal = !openDevModal;
			snapBack();
		}
	};

	const onEdgeTouchEnd = () => {
		dragTriggered = false;
		snapBack();
	};

	const onEdgeClick = () => {
		openDevModal = !openDevModal;
	};
</script>

{#if visible}
	<!-- Mobile / tablet: left-edge tab (reachable with full-screen / notch layouts) -->
	<button
		type="button"
		class="dev-edge"
		class:snapping
		aria-label="Toggle Dev Modal (pull right)"
		aria-pressed={openDevModal}
		style="transform: translateY(-50%) translateX({edgeTranslateX}px);"
		on:touchstart={onEdgeTouchStart}
		on:touchmove|nonpassive={onEdgeTouchMove}
		on:touchend={onEdgeTouchEnd}
		on:touchcancel={onEdgeTouchEnd}
		on:click={onEdgeClick}
	>
		<span class="dev-edge-grip" aria-hidden="true"></span>
		<span class="dev-edge-label" aria-hidden="true">DEV</span>
	</button>

	<!-- Desktop: hanging labeled tab -->
	<button
		type="button"
		class="dev-hanging"
		aria-label="Toggle Dev Modal"
		aria-pressed={openDevModal}
		on:click={() => (openDevModal = !openDevModal)}
	>
		<i class="fa-brands fa-empire dev-hanging-icon" aria-hidden="true"></i>
		<span class="dev-hanging-label">DEV</span>
	</button>

	<DevModal bind:openDevModal />
	<WidgetTypeDebugLegend />
	{#if $priorityLayoutDevHudKind === 'emeter'}
		<EmeterPriorityDevHud />
	{:else if $priorityLayoutDevHudKind === 'sensor'}
		<SensorPriorityDevHud />
	{:else if $priorityLayoutDevHudKind === 'relay'}
		<RelayPriorityDevHud />
	{:else if $priorityLayoutDevHudKind === 'light'}
		<LightPriorityDevHud />
	{/if}
{/if}

<style>
	.dev-edge,
	.dev-hanging {
		position: fixed;
		z-index: 2147483000;
		border: none;
		cursor: pointer;
		font-family: inherit;
		padding: 0;
		background: rgba(220, 38, 38, 0.85);
		color: #fff;
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
		transition:
			background 0.15s ease,
			opacity 0.15s ease;
	}

	.dev-edge:hover,
	.dev-hanging:hover {
		background: rgba(185, 28, 28, 0.95);
	}

	/* Mobile / tablet: left-edge pull tab */
	.dev-edge {
		display: none;
		top: 42%;
		left: 0;
		width: 56px;
		height: 72px;
		padding: 8px 6px 8px 8px;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		gap: 6px;
		border-top-right-radius: 12px;
		border-bottom-right-radius: 12px;
		touch-action: none;
		will-change: transform;
	}

	.dev-edge.snapping {
		transition: transform 0.22s cubic-bezier(0.2, 0.9, 0.3, 1.1);
	}

	.dev-edge-label {
		font-size: 10px;
		font-weight: 700;
		letter-spacing: 0.12em;
		line-height: 1;
		text-transform: uppercase;
		color: rgba(255, 255, 255, 0.95);
		writing-mode: vertical-rl;
		transform: rotate(180deg);
	}

	.dev-edge-grip {
		display: block;
		width: 3px;
		height: 22px;
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.9);
		flex-shrink: 0;
	}

	/* Desktop: hanging labeled tab above the main-menu logo (rail is 72px wide, 16px inset). */
	.dev-hanging {
		display: none;
		top: 0;
		left: 16px;
		width: var(--main-menu-width, 72px);
		justify-content: center;
		padding: 3px 8px 5px;
		border-bottom-left-radius: 10px;
		border-bottom-right-radius: 10px;
		align-items: center;
		gap: 6px;
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.06em;
		text-transform: uppercase;
	}

	.dev-hanging:hover {
		transform: translateY(1px);
	}

	.dev-hanging-icon {
		font-size: 12px;
	}

	.dev-hanging-label {
		line-height: 1;
	}

	@media (max-width: 1123px) {
		.dev-edge {
			display: inline-flex;
		}
	}

	@media (min-width: 1124px) {
		.dev-hanging {
			display: inline-flex;
		}
	}
</style>
